import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const FILTERS = [
  ["", "All"],
  ["accepted_for_send", "Accepted"],
  ["failed", "Failed"],
];

const formatEventTime = (value) => {
  if (!value) return "Time unavailable";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const eventLabel = (eventType) => String(eventType || "transactional").replaceAll("_", " ");

export const EmailDeliveryMonitor = () => {
  const [filter, setFilter] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadEvents = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await api.get("/admin/email-delivery-events", {
        params: filter ? { status: filter } : {},
      });
      setEvents(response.data.items || []);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return (
    <div className="w-full min-w-0 max-w-6xl" data-testid="email-delivery-monitor">
      <div
        className={[
          "flex flex-col justify-between gap-4 border-b border-[#E2DCD2] pb-6",
          "sm:flex-row sm:items-end",
        ].join(" ")}
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
            Transactional email
          </p>
          <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">
            Email Delivery Monitor
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[#64748B]">
            This records whether our SMTP server accepted a send attempt. It does not confirm inbox
            delivery, opens, or clicks.
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadEvents({ silent: true })}
          disabled={refreshing}
          className={[
            "inline-flex w-fit items-center gap-2 border border-[#2D5A47] px-4 py-2 text-sm",
            "font-semibold text-[#2D5A47] hover:bg-[#EBF2EE] disabled:opacity-50",
          ].join(" ")}
          data-testid="refresh-email-delivery-events"
        >
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <section
        className="mt-6 border border-[#E2DCD2] bg-[#FDFBF7] p-5"
        data-testid="email-delivery-events-panel"
      >
        <div className="flex flex-wrap gap-2" data-testid="email-delivery-filters">
          {FILTERS.map(([value, label]) => (
            <button
              key={value || "all"}
              type="button"
              onClick={() => setFilter(value)}
              className={`border px-3 py-2 text-xs font-semibold transition-colors ${
                filter === value
                  ? "border-[#2D5A47] bg-[#2D5A47] text-white"
                  : "border-[#E2DCD2] bg-white text-[#64748B] hover:border-[#2D5A47]"
              }`}
              data-testid={`email-delivery-filter-${value || "all"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div
            className="flex min-h-52 items-center justify-center text-sm text-[#64748B]"
            data-testid="email-delivery-events-loading"
          >
            <Loader2 className="mr-2 animate-spin" size={18} /> Loading email activity…
          </div>
        ) : events.length === 0 ? (
          <div
            className="py-14 text-center text-sm text-[#64748B]"
            data-testid="email-delivery-events-empty"
          >
            No email send attempts match this view yet.
          </div>
        ) : (
          <div className="mt-5 space-y-3" data-testid="email-delivery-events-list">
            {events.map((event) => {
              const failed = event.status === "failed";
              const Icon = failed ? TriangleAlert : CheckCircle2;
              return (
                <article
                  key={event.id}
                  className={[
                    "flex flex-col gap-3 border border-[#E2DCD2] bg-white p-4 sm:flex-row",
                    "sm:items-center sm:justify-between",
                  ].join(" ")}
                  data-testid={`email-delivery-event-${event.id}`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon
                      size={18}
                      className={
                        failed ? "mt-0.5 text-red-700" : "mt-0.5 text-emerald-700"
                      }
                      data-testid={`email-delivery-status-icon-${event.id}`}
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={[
                            "border px-2 py-1 text-[10px] font-bold uppercase tracking-wide",
                            failed
                              ? "border-red-200 bg-red-50 text-red-800"
                              : "border-emerald-200 bg-emerald-50 text-emerald-800",
                          ].join(" ")}
                          data-testid={`email-delivery-status-${event.id}`}
                        >
                          {failed ? "Failed" : "Accepted for send"}
                        </span>
                        <span
                          className="text-xs font-semibold capitalize text-[#8C6D46]"
                          data-testid={`email-delivery-type-${event.id}`}
                        >
                          {eventLabel(event.event_type)}
                        </span>
                      </div>
                      <p
                        className="mt-2 break-all text-sm font-medium text-[#1E2A24]"
                        data-testid={`email-delivery-recipient-${event.id}`}
                      >
                        {event.recipient_masked}
                      </p>
                      {failed && event.error_code ? (
                        <p
                          className="mt-1 text-xs font-semibold text-red-700"
                          data-testid={`email-delivery-error-${event.id}`}
                        >
                          {event.error_code.replaceAll("_", " ")}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <time
                    className="shrink-0 text-xs text-[#64748B]"
                    dateTime={event.created_at}
                    data-testid={`email-delivery-time-${event.id}`}
                  >
                    {formatEventTime(event.created_at)}
                  </time>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};